/**
 * ReplayTimelineBar — the replay tab's bottom bar (the StatusBar's slot
 * while a replay tab is active; the WorkflowStatusBar precedent of a
 * surface owning its own bar).
 *
 *  ┌──────────────────────────────────────────────────────────────────────────┐
 *  │ ⏮ ◀ ▶/⏸ ▶ ⏭  step 1204 / 380–940  [rate 4×]  ──track+brush──  [graph] 💾 ⬇ │ 🤖 │
 *  └──────────────────────────────────────────────────────────────────────────┘
 *
 * The track is a custom pointer-driven surface (no HTML slider anywhere in
 * the app): click/drag scrubs, the two brush handles narrow the viewing
 * range (the saved period), and the marker pins the cursor. Positions map
 * through the pure fraction math in logic.ts so the interaction is
 * unit-testable without a DOM. The RIGHT end carries the graph switcher
 * (multi-graph recordings) + save/download, then — after a separator — the
 * agent-decisions button (the robot; the same DecisionsModal the live
 * StatusBar opens).
 */
import { useCallback, useRef, useState } from 'react';
import {
  CaretLeft,
  CaretLineLeft,
  CaretRight,
  CaretLineRight,
  DownloadSimple,
  FloppyDisk,
  Pause,
  Play,
  Robot,
} from '@phosphor-icons/react';

import type { ReplaySession } from './useReplaySession';
import { stepAtFraction, trackFraction } from './logic';

/** One decision mark on the track: the step it happened at + the authoring
 *  agent's colour (palette.agentColor — the SAME colour the activity modal's
 *  badges use, so a colour means one agent in both places). */
export interface ReplayDecisionMark {
  step: number;
  color: string;
}

interface ReplayTimelineBarProps {
  session: ReplaySession;
  label: string;
  /** The recording's graphs — the switcher renders only when there are
   *  several (a single-graph recording needs no picker). */
  graphs?: { id: string; label: string }[];
  activeGraphId?: string;
  onSelectGraph?: (id: string) => void;
  /** Open the DecisionsModal (the same agent-activity modal the live
   *  StatusBar opens — fed the loaded window's aggregation). */
  onOpenDecisions: () => void;
  /** Agent decisions rendered as 1px vertical lines on the track, coloured
   *  per agent (the modal's badges share the colours). */
  decisionMarks?: ReplayDecisionMark[];
  onSave: () => void;
  onDownload: () => void;
  saving: boolean;
}

const RATES = [1, 2, 4, 8, 16];

export function ReplayTimelineBar({
  session,
  label,
  graphs,
  activeGraphId,
  onSelectGraph,
  onOpenDecisions,
  decisionMarks,
  onSave,
  onDownload,
  saving,
}: ReplayTimelineBarProps): JSX.Element {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragKind, setDragKind] = useState<null | 'cursor' | 'from' | 'to'>(null);
  const { range, cursor } = session;

  const fractionAt = useCallback((clientX: number): number => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  /** One shared drag loop: pointermove maps the pointer to a step and applies
   *  it to whatever handle (or the scrub) is being dragged. */
  const beginDrag = (kind: 'cursor' | 'from' | 'to') => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDragKind(kind);
    const apply = (kind: 'cursor' | 'from' | 'to', f: number) => {
      const step = stepAtFraction(f, range.from, range.to);
      if (kind === 'cursor') session.setCursor(step);
      else if (kind === 'from') session.setRange({ from: Math.min(step, range.to - 1), to: range.to });
      else session.setRange({ from: range.from, to: Math.max(step, range.from + 1) });
    };
    apply(kind, fractionAt(e.clientX));
    const onMove = (ev: PointerEvent) => apply(kind, fractionAt(ev.clientX));
    const onUp = () => {
      setDragKind(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const cursorF = cursor === null ? 0 : trackFraction(cursor, range.from, range.to);
  const fromF = trackFraction(range.from, range.from, range.to);
  const toF = trackFraction(range.to, range.from, range.to);
  const atStart = cursor !== null && cursor <= range.from;
  const atEnd = cursor !== null && cursor >= range.to;

  // Decision marks grouped per step: distinct colours deduped, and when
  // several agents decided on the SAME tick each colour gets a +1px offset so
  // the 1px lines sit side by side instead of hiding each other.
  const marksByStep = new Map<number, string[]>();
  for (const m of decisionMarks ?? []) {
    const step = Number(m.step);
    if (!(step >= range.from && step <= range.to)) continue;
    const colors = marksByStep.get(step) ?? [];
    if (!colors.includes(m.color)) colors.push(m.color);
    marksByStep.set(step, colors);
  }

  return (
    <div className="replay-bar" role="group" aria-label={`Replay timeline — ${label}`}>
      <div className="replay-controls">
        <button
          type="button"
          className="btn-icon-mini"
          title="Jump to range start"
          disabled={atStart}
          onClick={() => session.setCursor(range.from)}
        >
          <CaretLineLeft size={13} />
        </button>
        <button
          type="button"
          className="btn-icon-mini"
          title="Previous tick (←)"
          disabled={atStart}
          onClick={() => session.move(-1)}
        >
          <CaretLeft size={13} />
        </button>
        <button
          type="button"
          className="btn-icon-mini replay-play"
          title={session.playing ? 'Pause (Space)' : 'Play (Space)'}
          onClick={() => (session.playing ? session.pause() : session.play())}
        >
          {session.playing ? <Pause size={13} /> : <Play size={13} />}
        </button>
        <button
          type="button"
          className="btn-icon-mini"
          title="Next tick (→)"
          disabled={atEnd}
          onClick={() => session.move(1)}
        >
          <CaretRight size={13} />
        </button>
        <button
          type="button"
          className="btn-icon-mini"
          title="Jump to range end"
          disabled={atEnd}
          onClick={() => session.setCursor(range.to)}
        >
          <CaretLineRight size={13} />
        </button>
        <select
          className="replay-rate"
          title="Playback rate (ticks per second)"
          value={session.rate}
          onChange={(e) => session.setRate(Number(e.target.value))}
        >
          {RATES.map((r) => (
            <option key={r} value={r}>
              {r}×
            </option>
          ))}
        </select>
      </div>

      <span className="replay-step" title="Current tick / viewing range">
        step {cursor ?? '—'} · {range.from}–{range.to}
      </span>

      <div
        ref={trackRef}
        className={`replay-track${dragKind ? ` dragging-${dragKind}` : ''}`}
        onPointerDown={beginDrag('cursor')}
        role="slider"
        aria-label="Replay position"
        aria-valuemin={range.from}
        aria-valuemax={range.to}
        aria-valuenow={cursor ?? undefined}
        aria-valuetext={cursor === null ? 'not loaded' : `tick ${cursor}`}
      >
        {/* Loaded windows shade the track; unshaded spans are unrecorded
            (a gap) or not-yet-fetched territory. */}
        {session.loadedSpans.map((span) => {
          const a = trackFraction(Math.max(span.from, range.from), range.from, range.to);
          const b = trackFraction(Math.min(span.to, range.to), range.from, range.to);
          if (b <= a) return null;
          return (
            <div
              key={span.from}
              className="replay-track-loaded"
              style={{ left: `${a * 100}%`, width: `${(b - a) * 100}%` }}
            />
          );
        })}
        {/* Agent decisions: 1px vertical lines, one colour per agent (the
            activity modal's badges share the palette). */}
        {[...marksByStep.entries()].map(([step, colors]) =>
          colors.map((color, i) => (
            <div
              key={`${step}-${i}`}
              className="replay-decision-mark"
              style={{
                left: `${trackFraction(step, range.from, range.to) * 100}%`,
                background: color,
                transform: `translateX(${i}px)`,
              }}
              title={`Step ${step} — agent decision`}
            />
          )),
        )}
        {/* The narrowed viewing range (the brush body). */}
        <div className="replay-range" style={{ left: `${fromF * 100}%`, width: `${(toF - fromF) * 100}%` }} />
        <div
          className={`replay-handle replay-handle-from${dragKind === 'from' ? ' active' : ''}`}
          style={{ left: `${fromF * 100}%` }}
          onPointerDown={beginDrag('from')}
          role="slider"
          aria-label="Range start"
          aria-valuenow={range.from}
          title={`Range start (${range.from})`}
        />
        <div
          className={`replay-handle replay-handle-to${dragKind === 'to' ? ' active' : ''}`}
          style={{ left: `${toF * 100}%` }}
          onPointerDown={beginDrag('to')}
          role="slider"
          aria-label="Range end"
          aria-valuenow={range.to}
          title={`Range end (${range.to})`}
        />
        <div
          className="replay-marker"
          style={{ left: `${cursorF * 100}%` }}
          title={`Tick ${cursor ?? '—'}`}
        />
      </div>

      {(graphs?.length ?? 0) > 1 && (
        <select
          className="replay-rate"
          title="Which graph to render"
          value={activeGraphId ?? ''}
          onChange={(e) => onSelectGraph?.(e.target.value)}
        >
          {graphs!.map((g) => (
            <option key={g.id} value={g.id}>{g.label}</option>
          ))}
        </select>
      )}

      <div className="replay-actions">
        <button
          type="button"
          className="btn-icon-mini"
          title="Save this period as a replay doc (replays/<id>.json)"
          disabled={saving}
          onClick={onSave}
        >
          <FloppyDisk size={13} />
        </button>
        <button
          type="button"
          className="btn-icon-mini"
          title="Download the viewing range as a self-contained replay snapshot"
          onClick={onDownload}
        >
          <DownloadSimple size={13} />
        </button>
      </div>

      <span className="replay-bar-sep" aria-hidden="true" />

      <button
        type="button"
        className="btn-icon-mini replay-decisions-btn"
        title="Agent activity log (the loaded window)"
        onClick={onOpenDecisions}
      >
        <Robot size={14} />
      </button>
    </div>
  );
}
